import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';
import {
  Button, notification, Tag, Tooltip, Typography,
} from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { EntitySelect, RanchSelect } from 'farmx-web-ui';
import { actions, hooks, selectors } from 'farmx-redux-core';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { sensorApi } from 'farmx-api';
import { isMobile } from '../../../utils/detectDevice';
import './ruleSettingsPage.less';
import Page404 from 'components/app/Page404';
import EditableTable from './EditableTable';
import { useGetMaxWidth } from 'helper/common';

const { loadNewRanchList } = actions;
const { selectAllNewRanches, selectLoginUserInfo } = selectors;
const { useEntities } = hooks;

interface InstallationItem {
  identifier: string;
  type: string;
  node_state: string | null;
  install_state: string | null;
  visibility: boolean | null;
  online?: boolean | null;
  location?: {
    lat?: number | null;
    lng?: number | null;
  } | null;
  block_name?: string | null;
  associated_sensors?: string[];
  parent_device?: string | null;
  last_data_packet_timestamp?: string | null;
  data_packets_last_12_hours?: number | null;
  bad_data?: boolean | null;
}

interface InstallationResponse {
  ranch_id?: number;
  ranch_name?: string;
  items?: InstallationItem[];
}

interface RanchItem {
  id: number;
  name: string;
  entity?: {
    id: number;
  };
}

const installationTypePriority = [
  'lora_sensor_node',
  'field_node',
  'dragino_cellular',
  'lora_dragino',
  'pixl_soil',
  'aquacheck_soil',
];
const nodeDeviceTypes = ['lora_sensor_node', 'field_node', 'dragino_cellular', 'lora_dragino'];
const TAG_COLORS = {
  positive: 'green',
  negative: 'red',
  neutral: 'default',
  warning: 'orange',
} as const;

const getInstallationTypePriority = (type?: string | null) => {
  if (!type) return installationTypePriority.length + 1;
  const normalizedType = type.toLowerCase();
  const priorityIndex = installationTypePriority.indexOf(normalizedType);
  return priorityIndex === -1 ? installationTypePriority.length : priorityIndex;
};

const isNodeDeviceType = (type?: string | null) => (
  !!type && nodeDeviceTypes.includes(type.toLowerCase())
);

const formatTimestamp = (value?: string | null) => {
  if (!value) return '-';
  return moment(value).local().format('YYYY-MM-DD HH:mm:ss');
};

const renderRelativeTimestamp = (value?: string | null) => {
  if (!value) return '-';
  const formattedTime = formatTimestamp(value);
  return (
    <Tooltip title={formattedTime}>
      <span>{moment(value).local().fromNow()}</span>
    </Tooltip>
  );
};

const formatLocation = (value?: number | null) => {
  if (value === undefined || value === null || value === 0) return '-';
  return value;
};

const getBooleanTag = (value: boolean | null | undefined, trueLabel: string, falseLabel: string) => {
  if (value === undefined || value === null) return <span>-</span>;
  return <Tag color={value ? TAG_COLORS.positive : TAG_COLORS.neutral}>{value ? trueLabel : falseLabel}</Tag>;
};

const getBadDataTag = (value: boolean | null | undefined, trueLabel: string, falseLabel: string) => {
  if (value === undefined || value === null) return <span>-</span>;
  return <Tag color={value ? TAG_COLORS.negative : TAG_COLORS.positive}>{value ? trueLabel : falseLabel}</Tag>;
};

const getInstallStateTag = (value?: string | null) => {
  if (!value) return <span>-</span>;

  const normalizedValue = value.toLowerCase();
  const colorMap = {
    installed: TAG_COLORS.positive,
    pending: TAG_COLORS.warning,
    not_installed: TAG_COLORS.negative,
  };

  return <Tag color={colorMap[normalizedValue] || 'default'}>{value}</Tag>;
};

const getNodeStateTag = (value?: string | null) => {
  if (!value) return <span>-</span>;

  const normalizedValue = value.toLowerCase();
  const greenStates = ['healthy', 'provisioned'];
  const orangeStates = ['warning', 'incomplete', 'pending'];
  const redStates = ['failed', 'offline', 'error'];

  let color = TAG_COLORS.neutral;
  if (greenStates.includes(normalizedValue)) color = TAG_COLORS.positive;
  else if (orangeStates.includes(normalizedValue)) color = TAG_COLORS.warning;
  else if (redStates.includes(normalizedValue)) color = TAG_COLORS.negative;

  return <Tag color={color}>{value}</Tag>;
};

// Escapes values that would otherwise break the CSV structure.
const escapeCsvValue = (value: string | number | boolean | null | undefined) => {
  if (value === null || value === undefined) return '';
  const stringValue = String(value);
  if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

// Builds a flat CSV from the installation check rows currently loaded in the table.
const buildInstallationCheckCsv = (rows: InstallationItem[]) => {
  const headers = [
    'Identifier',
    'Type',
    'Data Packet Received',
    'Bad Data',
    'Packets Last 12h',
    'Online',
    'Install State',
    'Node State',
    'Visibility',
    'Block Name',
    'Associated Sensors',
    'Parent Device',
    'Latitude',
    'Longitude',
  ];

  const csvRows = rows.map((row) => ([
    row?.identifier || '',
    row?.type || '',
    row?.last_data_packet_timestamp || '',
    row?.bad_data ?? '',
    row?.data_packets_last_12_hours ?? '',
    row?.online ?? '',
    row?.install_state || '',
    row?.node_state || '',
    row?.visibility ?? '',
    row?.block_name || '',
    (row?.associated_sensors || []).join('; '),
    row?.parent_device || '',
    row?.location?.lat ?? '',
    row?.location?.lng ?? '',
  ].map((value) => escapeCsvValue(value)).join(',')));

  return [headers.join(','), ...csvRows].join('\n');
};

// Normalizes ranch names so the downloaded filename stays filesystem-safe.
const getSafeFileNamePart = (value?: string | null) => {
  if (!value) return '';
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

const InstallationCheck: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const maxWidth = useGetMaxWidth();
  const { activeEntityId, selectedEntity: resolvedEntity, entities } = useEntities();
  const allRanches = (useSelector(selectAllNewRanches) as RanchItem[]) || [];
  const userInfo: any = useSelector(selectLoginUserInfo)?.payload;
  const isAdmin = userInfo?.admin;

  const [selectedEntity, setSelectedEntity] = useState<number | undefined>(undefined);
  const [selectedRanch, setSelectedRanch] = useState<number | undefined>(undefined);
  const [responseData, setResponseData] = useState<InstallationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const effectiveEntityId = activeEntityId || resolvedEntity?.id;

  const ranches = useMemo(
    () => allRanches
      .filter((ranch) => ranch?.entity?.id === selectedEntity)
      .sort((a, b) => (a?.name || '').localeCompare(b?.name || '')),
    [selectedEntity, allRanches],
  );
  const selectedRanchName = useMemo(
    () => ranches.find((ranch) => ranch.id === selectedRanch)?.name,
    [ranches, selectedRanch],
  );

  const items = responseData?.items || [];
  const sortedItems = useMemo(
    () => [...items].sort((a, b) => {
      const priorityDiff = getInstallationTypePriority(a?.type) - getInstallationTypePriority(b?.type);
      if (priorityDiff !== 0) return priorityDiff;

      const typeDiff = (a?.type || '').localeCompare(b?.type || '');
      if (typeDiff !== 0) return typeDiff;

      return (a?.identifier || '').localeCompare(b?.identifier || '');
    }),
    [items],
  );

  useEffect(() => {
    if (!effectiveEntityId) return;
    setSelectedEntity(effectiveEntityId);
  }, [effectiveEntityId]);

  useEffect(() => {
    if (!selectedEntity) return;
    dispatch(loadNewRanchList(selectedEntity));
  }, [selectedEntity, dispatch]);

  useEffect(() => {
    if (!ranches?.length) {
      setSelectedRanch(undefined);
      return;
    }

    setSelectedRanch((currentRanch) => {
      if (currentRanch && ranches.some((ranch) => ranch.id === currentRanch)) {
        return currentRanch;
      }
      return ranches[0]?.id;
    });
  }, [ranches]);

  const fetchInstallationCheck = async (ranchId?: number) => {
    if (!ranchId) {
      setResponseData(null);
      return;
    }

    setLoading(true);
    try {
      if (typeof sensorApi.loadRanchInstallationCheck !== 'function') {
        throw new Error('sensorApi.loadRanchInstallationCheck is unavailable in the current bundle');
      }

      const response = await sensorApi.loadRanchInstallationCheck(ranchId);
      setResponseData(response?.data || null);
      setLastUpdated(moment().format('YYYY-MM-DD HH:mm:ss'));
    } catch (error) {
      // Helps us distinguish "helper missing in bundle" from a real HTTP error.
      console.error('Installation check request failed', error);
      setResponseData(null);
      notification.error({
        message: t('Failed to load installation check data'),
        maxCount: 1,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstallationCheck(selectedRanch);
  }, [selectedRanch]);

  const handleExport = async () => {
    if (!selectedRanch) {
      notification.warning({
        message: t('Please select a ranch first'),
        maxCount: 1,
      });
      return;
    }

    setExportLoading(true);
    try {
      const csvContent = buildInstallationCheckCsv(sortedItems);
      const safeRanchName = getSafeFileNamePart(responseData?.ranch_name || selectedRanchName);
      const fileName = safeRanchName
        ? `ranch_installation_check_${safeRanchName}.csv`
        : `ranch_installation_check_${selectedRanch}.csv`;
      const blobUrl = window.URL.createObjectURL(new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Installation check export failed', error);
      notification.error({
        message: t('Failed to export installation check data'),
        maxCount: 1,
      });
    } finally {
      setExportLoading(false);
    }
  };

  if (isMobile || !isAdmin) return <Page404 />;

  const columns: ColumnsType<InstallationItem> = [
    {
      title: t('Identifier'),
      dataIndex: 'identifier',
      key: 'identifier',
      width: 160,
    },
    {
      title: t('Type'),
      dataIndex: 'type',
      key: 'type',
      width: 100,
    },
    {
      title: t('Data Packet Received'),
      dataIndex: 'last_data_packet_timestamp',
      key: 'last_data_packet_timestamp',
      width: 150,
      render: (value) => renderRelativeTimestamp(value),
    },
    {
      title: t('Bad Data'),
      dataIndex: 'bad_data',
      key: 'bad_data',
      width: 100,
      render: (value) => getBadDataTag(value, t('True'), t('False')),
    },
    {
      title: t('Packets Last 12h'),
      dataIndex: 'data_packets_last_12_hours',
      key: 'data_packets_last_12_hours',
      width: 140,
      render: (value) => value ?? '-',
    },
    {
      title: t('Online'),
      dataIndex: 'online',
      key: 'online',
      width: 100,
      render: (value) => getBooleanTag(value, t('Online'), t('Offline')),
    },
    {
      title: t('Install State'),
      dataIndex: 'install_state',
      key: 'install_state',
      width: 120,
      render: (value) => getInstallStateTag(value),
    },
    {
      title: t('Node State'),
      dataIndex: 'node_state',
      key: 'node_state',
      width: 120,
      render: (value, record) => (
        isNodeDeviceType(record?.type) ? getNodeStateTag(value) : <span>N/A</span>
      ),
    },
    {
      title: t('Visible'),
      dataIndex: 'visibility',
      key: 'visibility',
      width: 100,
      render: (value) => getBooleanTag(value, t('Yes'), t('No')),
    },
    {
      title: t('Block'),
      dataIndex: 'block_name',
      key: 'block_name',
      width: 120,
      render: (value) => value || '-',
    },
    {
      title: t('Associated Sensors'),
      dataIndex: 'associated_sensors',
      key: 'associated_sensors',
      width: 140,
      render: (value: string[]) => value?.length ? value.join(', ') : '-',
    },
    {
      title: t('Parent Device'),
      dataIndex: 'parent_device',
      key: 'parent_device',
      width: 140,
      render: (value) => value || '-',
    },
    {
      title: t('Latitude'),
      dataIndex: 'location',
      key: 'lat',
      width: 100,
      render: (value) => formatLocation(value?.lat),
    },
    {
      title: t('Longitude'),
      dataIndex: 'location',
      key: 'lng',
      width: 100,
      render: (value) => formatLocation(value?.lng),
    },
  ];

  return (
    <div
      className="single-column-page padded desktop-container desktop-container-config"
      style={!isMobile ? { maxWidth } : {}}
    >
      <div className="flex-space-between align-baseline">
        <Typography.Title level={3}>
          {t('Installation Check Report')}
        </Typography.Title>
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => fetchInstallationCheck(selectedRanch)}
        >
          {t('Refresh')}
        </Button>
      </div>

      <div>
        {lastUpdated && (
          <div className="last-update display-block">
            <span className="font-bold">{`${t('Report Generated At')}: `}</span>
            <span>{lastUpdated}</span>
          </div>
        )}
      </div>

      <div className="installation-check-controls">
        <div className="installation-check-control installation-check-control-entity">
          <span className="font-bold margin-right-10">{`${t('Entity')}: `}</span>
          <EntitySelect
            value={selectedEntity}
            items={entities}
            onChange={(value) => setSelectedEntity(value)}
            placeholder={t('Select entity')}
          />
        </div>

        <div
          id="ranch-select-div"
          className="installation-check-control installation-check-control-ranch"
        >
          <span className="font-bold margin-right-10">{`${t('Ranch')}: `}</span>
          <RanchSelect
            value={selectedRanch}
            items={ranches}
            onChange={setSelectedRanch}
            placeholder={t('Select ranch')}
          />
        </div>

        <div className="installation-check-control installation-check-control-export">
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={exportLoading}
            onClick={handleExport}
          >
            {t('Export CSV')}
          </Button>
        </div>
      </div>

      <div className="installation-check-table">
        <EditableTable
          data={sortedItems.map((item) => ({ ...item, key: item.identifier }))}
          columns={columns}
          showIsActive={false}
          showAction={false}
          loading={loading}
          isDataSorted
          hidePagination
          rowClassName={(_, index) => (
            index % 2 === 0 ? '' : 'table-row-dark'
          )}
        />
      </div>
    </div>
  );
};

export default InstallationCheck;
